import * as Sentry from '@sentry/ember';
import { Event, EventHint, Breadcrumb } from '@sentry/types';
import { httpClientIntegration, extraErrorDataIntegration } from '@sentry/integrations';
import config from 'cio/config/environment';

const ignoreErrors = [
  // Ignore `TaskCancelation` messages from ember-concurrency
  'TaskCancelation', // eslint-disable-line spellcheck/spell-checker

  // Ignore cross-origin frame messages, almost definitely from adblock
  `Failed to read the 'contentDocument' property from 'HTMLIFrameElement'`,

  // Almost all of our errors are 404s caused by async associations to
  // customers or drafts which have now been deleted. These are flooding our
  // Sentry account and we can't do anything about them, so suppress the
  // reports for now.
  /^Ember Data Request GET/,

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Dead_object
  // Some customers may use browser addons to perform unsupported activities -
  // Firefox in particular has this special TypeError that may spam us with
  // unnecessary Sentry calls if not silenced
  `can't access dead object`,

  // Parcel
  `Cannot read properties of undefined (reading 'getModel')`,
  'setVariables timed out after 10000ms',
  /t.monaco.editor/,

  // Ignore fetch errors, since they're ambiguous and don't provide any helpful detail
  // related issue: https://github.com/getsentry/sentry-javascript/issues/4146
  /Failed to fetch/,

  /TransitionAborted/,
];

function shouldSentryWork() {
  return config.isStandalone || config.isProdStaging || config.isProduction;
}

function beforeBreadcrumb(breadcrumb: Breadcrumb) {
  if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
    return null;
  }

  return breadcrumb;
}

export function beforeSend(event: Event, hint: EventHint) {
  let error = hint.originalException;

  // filter out UnhandledRejection errors that have no information
  // example error: https://sentry.io/organizations/customerio/issues/2475399114/?environment=production&project=86254&query=is%3Aunresolved&sort=freq&statsPeriod=14d
  // github issue: https://github.com/getsentry/sentry-javascript/issues/3440
  if (event?.exception?.values?.length === 1) {
    let [exception] = event.exception.values;
    if (
      exception.type === 'UnhandledRejection' &&
      exception.value?.includes('Non-Error promise rejection captured with value: Object Not Found Matching Id:')
    ) {
      return null;
    }
  }

  // If Fullstory is running, send the event to Fullstory too.
  if (error instanceof Error && window.FS?.event != null) {
    // Working production code accesses some untyped "error" properties
    // Define custom type with them and cast "error" to it so it type-checks correctly:
    let errorTyped = error as Error & { fileName: string; lineNumber: string };

    window.FS.event('Application Error', {
      name: errorTyped.name,
      message: errorTyped.message,
      fileName: errorTyped.fileName,
      lineNumber: errorTyped.lineNumber,
      stack: errorTyped.stack,
      sentryEventId: hint.event_id,
    });
  }

  return event;
}

export function setMasqueradingContext(isMasqOn: boolean) {
  if (shouldSentryWork()) {
    Sentry.setExtra('masquerading_enabled', isMasqOn);
  }
}

export function startSentry() {
  if (!shouldSentryWork()) {
    return;
  }

  // Allow errors from scripts loaded from our CDN or our domain
  let allowUrls: Sentry.BrowserOptions['allowUrls'] = [/https:\/\/(unityassets|fly)\.customer\.io/];

  if (config.publicAssetUrl != null) {
    allowUrls = [...allowUrls, config.publicAssetUrl];
  }

  Sentry.init({
    integrations: [
      extraErrorDataIntegration(),
      httpClientIntegration({
        failedRequestStatusCodes: [
          [405, 408],
          [410, 499],
          [501, 599],
        ],
      }),
    ],
    environment: config.cioEnvironment,
    release: `journeys-ui@${config.APP.version}+${config.cioEnvironment}`,
    ignoreErrors,
    beforeBreadcrumb,
    beforeSend,
    autoSessionTracking: false,
    denyUrls: [
      // Filter out extension scripts
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
    allowUrls,
    ...config.sentry,
  });
}
